import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/shared/infrastructure/stores';
import { oauth2Service } from '@/features/auth/infrastructure/api/oauth2.service';
import { authApi } from '@/features/auth/infrastructure/api/auth.api';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const initialHasTokens = !!oauth2Service.getTokens();
  const [bootstrapping, setBootstrapping] = useState<boolean>(() => initialHasTokens && !isAuthenticated);
  const setUser = useAuthStore((state) => state.setUser);

  // On first mount, if there are stored tokens, validate and keep session
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (isAuthenticated) return;
      const tokens = initialHasTokens ? oauth2Service.getTokens() : null;
      if (!tokens) return;

      try {
        setBootstrapping(true);
        const userDTO = await authApi.getCurrentUser();
        if (!mounted) return;
        if (userDTO) {
          setUser({
            id: userDTO.id,
            name: userDTO.name,
            email: userDTO.email,
            role: userDTO.role,
          });
        }
      } catch (error) {
        // authApi se encarga de limpiar tokens inválidos
      } finally {
        if (mounted) setBootstrapping(false);
      }
    })();
    return () => { mounted = false; };
  }, [isAuthenticated, setUser, initialHasTokens]);

  if (bootstrapping) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
